// File upload utilities with validation and error handling

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
export const ALLOWED_DOCUMENT_TYPES = ['application/pdf', ...ALLOWED_IMAGE_TYPES];

export class FileValidationError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'FileValidationError';
        this.code = code;
    }
}

/**
 * Validate a file before upload
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result
 */
export function validateFile(file, options = {}) {
    const {
        maxSize = MAX_FILE_SIZE,
        allowedTypes = ALLOWED_IMAGE_TYPES,
        required = false
    } = options;

    if (!file) {
        if (required) {
            throw new FileValidationError('Bestand is verplicht', 'FILE_REQUIRED');
        }
        return { valid: true };
    }

    if (!(file instanceof File)) {
        throw new FileValidationError('Ongeldig bestandsobject', 'INVALID_FILE_OBJECT');
    }

    // Check file size
    if (file.size > maxSize) {
        const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
        throw new FileValidationError(
            `Bestand is te groot. Maximum grootte is ${maxSizeMB}MB`,
            'FILE_TOO_LARGE'
        );
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
        const allowedExtensions = allowedTypes
            .map(type => type.split('/')[1])
            .join(', ');
        throw new FileValidationError(
            `Bestandstype niet toegestaan. Toegestane types: ${allowedExtensions}`,
            'INVALID_FILE_TYPE'
        );
    }

    return { valid: true, file };
}

/**
 * Create a preview URL for an image file
 * @param {File} file - The image file
 * @returns {string} Object URL for preview
 */
export function createPreviewUrl(file) {
    if (!file || !(file instanceof File)) return null;
    return URL.createObjectURL(file);
}

/**
 * Revoke object URLs to prevent memory leaks
 * @param {string|string[]} urls - URL(s) to revoke
 */
export function revokePreviewUrls(urls) {
    const urlArray = Array.isArray(urls) ? urls : [urls];
    urlArray.forEach(url => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    });
}

/**
 * Build FormData with proper field handling
 * @param {Object} data - Data object to convert
 * @param {Array} fileFields - Array of file field configurations
 * @returns {FormData}
 */
export function buildFormData(data, fileFields = []) {
    const formData = new FormData();

    // Add regular fields
    Object.entries(data).forEach(([key, value]) => {
        // Skip file fields - they're handled separately
        if (fileFields.some(f => f.dataKey === key)) return;

        if (value === null || value === undefined) {
            return; // Skip null/undefined
        }

        // Serialize objects/arrays as JSON
        if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value));
        } else {
            formData.append(key, String(value));
        }
    });

    // Add file fields
    fileFields.forEach(({ dataKey, files, formKey }) => {
        const fileArray = Array.isArray(files) ? files : [files];

        fileArray.forEach((file, index) => {
            if (file && file instanceof File) {
                const key = formKey
                    ? `${formKey}_${index}`
                    : `${dataKey}_${index}`;
                formData.append(key, file);
            }
        });
    });

    return formData;
}

/**
 * Upload files with progress tracking
 * @param {string} url - Upload endpoint
 * @param {FormData} formData - Form data to upload
 * @param {Function} onProgress - Progress callback (percent)
 * @param {Object} axiosInstance - Axios instance
 * @returns {Promise}
 */
export async function uploadWithProgress(url, formData, onProgress, axiosInstance) {
    return axiosInstance.post(url, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                onProgress?.(percentCompleted);
            }
        },
    });
}

/**
 * Compress image file before upload (optional optimization)
 * @param {File} file - Image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>}
 */
export async function compressImage(file, options = {}) {
    const {
        maxWidth = 1920,
        maxHeight = 1920,
        quality = 0.8
    } = options;

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();

            img.onload = () => {
                const canvas = document.createElement('canvas');
                let { width, height } = img;

                // Calculate new dimensions
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width *= ratio;
                    height *= ratio;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(
                    (blob) => {
                        const compressedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });
                        resolve(compressedFile);
                    },
                    file.type,
                    quality
                );
            };

            img.onerror = () => reject(new Error('Fout bij laden van afbeelding'));
            img.src = e.target.result;
        };

        reader.onerror = () => reject(new Error('Fout bij lezen van bestand'));
        reader.readAsDataURL(file);
    });
}

/**
 * Batch validate multiple files
 * @param {File[]} files - Array of files to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation results
 */
export function validateFiles(files, options = {}) {
    const results = {
        valid: [],
        invalid: [],
        errors: []
    };

    files.forEach((file, index) => {
        try {
            const result = validateFile(file, options);
            if (result.valid) {
                results.valid.push({ file, index });
            }
        } catch (error) {
            results.invalid.push({ file, index });
            results.errors.push({ index, error: error.message, code: error.code });
        }
    });

    return results;
}

